// Custom Shape Tool — create shapes from preset custom paths.
// Photoshop: U → Custom Shape Tool.

import { screenToDoc } from "../render/Viewport";
import { createRectPath, createEllipsePath, createPolygonPath, createStarPath, createCustomShapePath } from "../document/PathModel";
import type { PathModel } from "../document/PathModel";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type CustomShapePreset =
  | "heart"
  | "star-5"
  | "star-6"
  | "star-8"
  | "flower"
  | "arrow-right"
  | "arrow-left"
  | "arrow-up"
  | "arrow-down"
  | "speech-bubble"
  | "cloud"
  | "lightning"
  | "checkmark"
  | "cross"
  | "circle-badge"
  | "shield"
  | "gear"
  | "moon"
  | "sun"
  | "bookmark";

/**
 * Canonical, ordered list of every preset the Custom Shape tool supports.
 * Single source of truth for UI that needs to enumerate the shape library
 * (e.g. the Shapes panel picker grid and the options-bar preset dropdown).
 */
export const CUSTOM_SHAPE_PRESETS: readonly CustomShapePreset[] = [
  "heart", "star-5", "star-6", "star-8", "flower", "arrow-right", "arrow-left",
  "arrow-up", "arrow-down", "speech-bubble", "cloud", "lightning", "checkmark",
  "cross", "circle-badge", "shield", "gear", "moon", "sun", "bookmark",
];

export interface CustomShapeToolOptions {
  id: string;
  preset?: CustomShapePreset;
}

export class CustomShapeTool implements Tool {
  readonly id: string;
  readonly shortcut = "U";
  private start: { x: number; y: number } | null = null;
  private lastPath: PathModel | null = null;

  preset: CustomShapePreset = "heart";
  fillColor: { r: number; g: number; b: number; a: number } | null = { r: 100, g: 150, b: 255, a: 1 };
  strokeColor: { r: number; g: number; b: number; a: number } | null = null;
  strokeWidth = 0;

  constructor(opts: CustomShapeToolOptions) {
    this.id = opts.id;
    if (opts.preset) this.preset = opts.preset;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    this.start = { x: p.x, y: p.y };
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !this.start) return;
    let p = screenToDoc(ctx.viewport, s.x, s.y);
    let x = Math.min(this.start.x, p.x);
    let y = Math.min(this.start.y, p.y);
    let w = Math.abs(p.x - this.start.x);
    let h = Math.abs(p.y - this.start.y);

    if (s.shiftKey) {
      const m = Math.max(w, h);
      w = m; h = m;
      x = this.start.x - m / 2;
      y = this.start.y - m / 2;
    }
    if (s.altKey) {
      const cx = this.start.x;
      const cy = this.start.y;
      w = Math.abs(p.x - cx) * 2;
      h = Math.abs(p.y - cy) * 2;
      x = cx - w / 2;
      y = cy - h / 2;
      if (s.shiftKey) {
        const m = Math.max(w, h);
        w = m; h = m;
        x = cx - m / 2;
        y = cy - m / 2;
      }
    }

    if (w < 1 && h < 1) { this.start = null; return; }

    const cx = x + w / 2;
    const cy = y + h / 2;
    const radius = Math.min(w, h) / 2;

    this.lastPath = this.buildShape(cx, cy, radius, w, h);
    ctx.requestRender();
    this.start = null;
  }

  private buildShape(cx: number, cy: number, radius: number, w: number, h: number): PathModel {
    switch (this.preset) {
      case "heart":
        return createCustomShapePath([
          { cmd: "M", args: [cx, cy + radius * 0.8] },
          { cmd: "C", args: [cx - radius * 1.2, cy - radius * 0.2, cx - radius * 0.5, cy - radius * 1.0, cx, cy - radius * 0.4] },
          { cmd: "C", args: [cx + radius * 0.5, cy - radius * 1.0, cx + radius * 1.2, cy - radius * 0.2, cx, cy + radius * 0.8] },
          { cmd: "Z", args: [] },
        ]);
      case "star-5":
        return createStarPath(cx, cy, radius, radius * 0.4, 5);
      case "star-6":
        return createStarPath(cx, cy, radius, radius * 0.5, 6);
      case "star-8":
        return createStarPath(cx, cy, radius, radius * 0.5, 8);
      case "flower":
        return createCustomShapePath(this.buildFlower(cx, cy, radius));
      case "arrow-right":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius, cy - radius * 0.5] },
          { cmd: "L", args: [cx + radius * 0.4, cy - radius * 0.5] },
          { cmd: "L", args: [cx + radius * 0.4, cy - radius] },
          { cmd: "L", args: [cx + radius, cy] },
          { cmd: "L", args: [cx + radius * 0.4, cy + radius] },
          { cmd: "L", args: [cx + radius * 0.4, cy + radius * 0.5] },
          { cmd: "L", args: [cx - radius, cy + radius * 0.5] },
          { cmd: "Z", args: [] },
        ]);
      case "arrow-left":
        return createCustomShapePath([
          { cmd: "M", args: [cx + radius, cy - radius * 0.5] },
          { cmd: "L", args: [cx - radius * 0.4, cy - radius * 0.5] },
          { cmd: "L", args: [cx - radius * 0.4, cy - radius] },
          { cmd: "L", args: [cx - radius, cy] },
          { cmd: "L", args: [cx - radius * 0.4, cy + radius] },
          { cmd: "L", args: [cx - radius * 0.4, cy + radius * 0.5] },
          { cmd: "L", args: [cx + radius, cy + radius * 0.5] },
          { cmd: "Z", args: [] },
        ]);
      case "arrow-up":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.5, cy + radius] },
          { cmd: "L", args: [cx - radius * 0.5, cy - radius * 0.4] },
          { cmd: "L", args: [cx - radius, cy - radius * 0.4] },
          { cmd: "L", args: [cx, cy - radius] },
          { cmd: "L", args: [cx + radius, cy - radius * 0.4] },
          { cmd: "L", args: [cx + radius * 0.5, cy - radius * 0.4] },
          { cmd: "L", args: [cx + radius * 0.5, cy + radius] },
          { cmd: "Z", args: [] },
        ]);
      case "arrow-down":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.5, cy - radius] },
          { cmd: "L", args: [cx - radius * 0.5, cy + radius * 0.4] },
          { cmd: "L", args: [cx - radius, cy + radius * 0.4] },
          { cmd: "L", args: [cx, cy + radius] },
          { cmd: "L", args: [cx + radius, cy + radius * 0.4] },
          { cmd: "L", args: [cx + radius * 0.5, cy + radius * 0.4] },
          { cmd: "L", args: [cx + radius * 0.5, cy - radius] },
          { cmd: "Z", args: [] },
        ]);
      case "speech-bubble":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.8, cy - radius * 0.6] },
          { cmd: "Q", args: [cx - radius, cy - radius * 0.6, cx - radius, cy - radius * 0.2] },
          { cmd: "L", args: [cx - radius, cy + radius * 0.2] },
          { cmd: "Q", args: [cx - radius, cy + radius * 0.6, cx - radius * 0.8, cy + radius * 0.6] },
          { cmd: "L", args: [cx - radius * 0.3, cy + radius * 0.6] },
          { cmd: "L", args: [cx - radius * 0.5, cy + radius] },
          { cmd: "L", args: [cx, cy + radius * 0.6] },
          { cmd: "L", args: [cx + radius * 0.8, cy + radius * 0.6] },
          { cmd: "Q", args: [cx + radius, cy + radius * 0.6, cx + radius, cy + radius * 0.2] },
          { cmd: "L", args: [cx + radius, cy - radius * 0.2] },
          { cmd: "Q", args: [cx + radius, cy - radius * 0.6, cx + radius * 0.8, cy - radius * 0.6] },
          { cmd: "Z", args: [] },
        ]);
      case "cloud":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.6, cy + radius * 0.3] },
          { cmd: "C", args: [cx - radius * 1.2, cy + radius * 0.3, cx - radius * 1.2, cy - radius * 0.3, cx - radius * 0.6, cy - radius * 0.3] },
          { cmd: "C", args: [cx - radius * 0.8, cy - radius * 0.8, cx - radius * 0.2, cy - radius * 1.0, cx + radius * 0.1, cy - radius * 0.6] },
          { cmd: "C", args: [cx + radius * 0.4, cy - radius * 1.0, cx + radius * 1.0, cy - radius * 0.8, cx + radius * 0.8, cy - radius * 0.2] },
          { cmd: "C", args: [cx + radius * 1.3, cy - radius * 0.2, cx + radius * 1.3, cy + radius * 0.4, cx + radius * 0.8, cy + radius * 0.4] },
          { cmd: "L", args: [cx - radius * 0.6, cy + radius * 0.4] },
          { cmd: "Z", args: [] },
        ]);
      case "lightning":
        return createCustomShapePath([
          { cmd: "M", args: [cx + radius * 0.2, cy - radius] },
          { cmd: "L", args: [cx - radius * 0.3, cy - radius * 0.1] },
          { cmd: "L", args: [cx + radius * 0.1, cy - radius * 0.1] },
          { cmd: "L", args: [cx - radius * 0.2, cy + radius] },
          { cmd: "L", args: [cx + radius * 0.3, cy + radius * 0.1] },
          { cmd: "L", args: [cx - radius * 0.1, cy + radius * 0.1] },
          { cmd: "Z", args: [] },
        ]);
      case "checkmark":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.8, cy] },
          { cmd: "L", args: [cx - radius * 0.3, cy + radius * 0.5] },
          { cmd: "L", args: [cx + radius * 0.8, cy - radius * 0.5] },
          { cmd: "L", args: [cx + radius * 0.8, cy - radius * 0.2] },
          { cmd: "L", args: [cx - radius * 0.3, cy + radius * 0.8] },
          { cmd: "L", args: [cx - radius * 0.8, cy + radius * 0.3] },
          { cmd: "Z", args: [] },
        ]);
      case "cross":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.2, cy - radius] },
          { cmd: "L", args: [cx + radius * 0.2, cy - radius] },
          { cmd: "L", args: [cx + radius * 0.2, cy - radius * 0.2] },
          { cmd: "L", args: [cx + radius, cy - radius * 0.2] },
          { cmd: "L", args: [cx + radius, cy + radius * 0.2] },
          { cmd: "L", args: [cx + radius * 0.2, cy + radius * 0.2] },
          { cmd: "L", args: [cx + radius * 0.2, cy + radius] },
          { cmd: "L", args: [cx - radius * 0.2, cy + radius] },
          { cmd: "L", args: [cx - radius * 0.2, cy + radius * 0.2] },
          { cmd: "L", args: [cx - radius, cy + radius * 0.2] },
          { cmd: "L", args: [cx - radius, cy - radius * 0.2] },
          { cmd: "L", args: [cx - radius * 0.2, cy - radius * 0.2] },
          { cmd: "Z", args: [] },
        ]);
      case "circle-badge":
        return createEllipsePath(cx, cy, radius, radius);
      case "shield":
        return createCustomShapePath([
          { cmd: "M", args: [cx, cy - radius] },
          { cmd: "L", args: [cx + radius * 0.8, cy - radius * 0.6] },
          { cmd: "L", args: [cx + radius * 0.8, cy + radius * 0.1] },
          { cmd: "C", args: [cx + radius * 0.8, cy + radius * 0.6, cx + radius * 0.3, cy + radius * 0.9, cx, cy + radius] },
          { cmd: "C", args: [cx - radius * 0.3, cy + radius * 0.9, cx - radius * 0.8, cy + radius * 0.6, cx - radius * 0.8, cy + radius * 0.1] },
          { cmd: "L", args: [cx - radius * 0.8, cy - radius * 0.6] },
          { cmd: "Z", args: [] },
        ]);
      case "gear":
        return createCustomShapePath(this.buildGear(cx, cy, radius));
      case "moon":
        return createCustomShapePath([
          { cmd: "M", args: [cx + radius * 0.3, cy - radius * 0.8] },
          { cmd: "C", args: [cx - radius * 0.5, cy - radius * 1.0, cx - radius * 1.0, cy - radius * 0.3, cx - radius * 0.8, cy + radius * 0.3] },
          { cmd: "C", args: [cx - radius * 0.6, cy + radius * 0.8, cx, cy + radius * 1.0, cx + radius * 0.5, cy + radius * 0.6] },
          { cmd: "C", args: [cx + radius * 0.2, cy + radius * 0.3, cx + radius * 0.1, cy - radius * 0.2, cx + radius * 0.3, cy - radius * 0.8] },
          { cmd: "Z", args: [] },
        ]);
      case "sun":
        return createCustomShapePath(this.buildSun(cx, cy, radius));
      case "bookmark":
        return createCustomShapePath([
          { cmd: "M", args: [cx - radius * 0.5, cy - radius] },
          { cmd: "L", args: [cx + radius * 0.5, cy - radius] },
          { cmd: "L", args: [cx + radius * 0.5, cy + radius * 0.5] },
          { cmd: "L", args: [cx, cy + radius * 0.2] },
          { cmd: "L", args: [cx - radius * 0.5, cy + radius * 0.5] },
          { cmd: "Z", args: [] },
        ]);
      default:
        return createPolygonPath(cx, cy, radius, 5);
    }
  }

  private buildFlower(cx: number, cy: number, r: number): Array<{ cmd: string; args: number[] }> {
    const cmds: Array<{ cmd: string; args: number[] }> = [];
    const petals = 6;
    for (let i = 0; i < petals; i++) {
      const angle = (i / petals) * Math.PI * 2;
      const px = cx + Math.cos(angle) * r * 0.6;
      const py = cy + Math.sin(angle) * r * 0.6;
      if (i === 0) {
        cmds.push({ cmd: "M", args: [cx, cy] });
      }
      cmds.push({
        cmd: "C",
        args: [
          px + Math.cos(angle + 0.5) * r * 0.5,
          py + Math.sin(angle + 0.5) * r * 0.5,
          px + Math.cos(angle - 0.5) * r * 0.5,
          py + Math.sin(angle - 0.5) * r * 0.5,
          cx, cy,
        ],
      });
    }
    cmds.push({ cmd: "Z", args: [] });
    return cmds;
  }

  private buildGear(cx: number, cy: number, r: number): Array<{ cmd: string; args: number[] }> {
    const cmds: Array<{ cmd: string; args: number[] }> = [];
    const teeth = 12;
    const innerR = r * 0.75;
    cmds.push({ cmd: "M", args: [cx + innerR, cy] });
    for (let i = 0; i < teeth; i++) {
      const a1 = (i / teeth) * Math.PI * 2;
      const a2 = ((i + 0.3) / teeth) * Math.PI * 2;
      const a3 = ((i + 0.5) / teeth) * Math.PI * 2;
      const a4 = ((i + 0.8) / teeth) * Math.PI * 2;
      cmds.push({ cmd: "L", args: [cx + Math.cos(a1) * innerR, cy + Math.sin(a1) * innerR] });
      cmds.push({ cmd: "L", args: [cx + Math.cos(a2) * r, cy + Math.sin(a2) * r] });
      cmds.push({ cmd: "L", args: [cx + Math.cos(a3) * r, cy + Math.sin(a3) * r] });
      cmds.push({ cmd: "L", args: [cx + Math.cos(a4) * innerR, cy + Math.sin(a4) * innerR] });
    }
    cmds.push({ cmd: "Z", args: [] });
    return cmds;
  }

  private buildSun(cx: number, cy: number, r: number): Array<{ cmd: string; args: number[] }> {
    const cmds: Array<{ cmd: string; args: number[] }> = [];
    const rays = 12;
    const innerR = r * 0.6;
    cmds.push({ cmd: "M", args: [cx + innerR, cy] });
    for (let i = 0; i < rays; i++) {
      const a1 = (i / rays) * Math.PI * 2;
      const a2 = ((i + 0.3) / rays) * Math.PI * 2;
      cmds.push({ cmd: "L", args: [cx + Math.cos(a1) * innerR, cy + Math.sin(a1) * innerR] });
      cmds.push({ cmd: "L", args: [cx + Math.cos(a2) * r, cy + Math.sin(a2) * r] });
    }
    cmds.push({ cmd: "Z", args: [] });
    return cmds;
  }

  getLastPath(): PathModel | null {
    return this.lastPath;
  }

  /** Set the active preset (e.g. from the Shapes panel picker or options bar). */
  setPreset(preset: CustomShapePreset): void {
    this.preset = preset;
  }

  /**
   * Return true while a shape drag (pointerdown...pointerup) is in progress
   * — used by the host's generic Escape-mid-drag routing (i187), the same
   * pattern ShapeTool already implements.
   */
  isDragging(): boolean {
    return this.start !== null;
  }

  /**
   * Cancel the in-progress shape drag (Escape key mid-drag, i187). Simply
   * drops the pending anchor — no path has been committed to `lastPath` yet
   * at this point, so there's nothing to roll back beyond forgetting the
   * drag-in-progress state and requesting a re-render to clear any preview.
   */
  cancel(ctx: ToolContext): void {
    this.start = null;
    ctx.requestRender();
  }
}
